import {
  Banner,
  EBannerBackgroundVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  GeneralCompassIcon,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useRouter } from '@/hooks/useRouter';
import { EToastVariant, useToast } from '@/hooks/useToast';
import { getUserCurrency } from '@/redux/selectors/currency';
import { trackSmartMatchRequestToBookClickedEvent } from '@/services/analytics/trips';
import { createBidMatchQuote } from '@/services/smartMatch/createBidMatchQuote';
import { declineBidMatch } from '@/services/smartMatch/declineBidMatch';
import type { IBid } from '@/services/types/bids/bids';

import BidMatchCard from './BidMatchCard';

type TBidMatchCardContainerProps = {
  bid: IBid;
  onBidMatchDeclined?: () => void;
};

const BidMatchCardContainer = ({ bid, onBidMatchDeclined }: TBidMatchCardContainerProps) => {
  const intl = useIntl();
  const { showToast } = useToast();
  const router = useRouter();
  const currency = useSelector(getUserCurrency);

  const handleDecline = useCallback(
    async (matchId: number) => {
      try {
        await declineBidMatch(bid.id, matchId);
        onBidMatchDeclined?.();
      } catch {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({
            id: 'EydagZ',
            defaultMessage: 'Something went wrong',
            description: 'Trips > Smart Match > Decline error toast title',
          }),
          intl.formatMessage({
            id: 'cB+LdY',
            defaultMessage: 'We couldn\u2019t decline this match. Please try again.',
            description: 'Trips > Smart Match > Decline error toast message',
          }),
        );
      }
    },
    [bid.id, onBidMatchDeclined, showToast, intl],
  );

  const handleRequestToBook = useCallback(
    async (matchId: number, rentalId: number, instantBook: boolean) => {
      trackSmartMatchRequestToBookClickedEvent({
        bidID: bid.id,
        bidMatchID: matchId,
        rentalID: rentalId,
        instantBook,
      });

      try {
        const quote = await createBidMatchQuote(bid, rentalId, currency);
        await router.push(`/checkout/${rentalId}/new?quote=${quote.id}`);
      } catch {
        showToast(
          EToastVariant.Error,
          intl.formatMessage({
            id: 'EydagZ',
            defaultMessage: 'Something went wrong',
            description: 'Trips > Smart Match > Decline error toast title',
          }),
          intl.formatMessage({
            id: 'bGMyen',
            defaultMessage: 'We couldn’t start your booking. Please try again.',
            description: 'Trips > Smart Match > Request to book error toast message',
          }),
        );
      }
    },
    [bid, currency, router, showToast, intl],
  );

  if (!bid.bid_matches?.length) return null;

  return (
    <div className="mx-4 mb-8 md:mx-0 md:px-8 xl:px-16">
      <Banner
        withBoxShadow={false}
        withWhiteBorder={false}
        backgroundVariant={EBannerBackgroundVariant.Neutral}
        className="flex flex-row items-start px-5 py-4 mb-8 gap-4">
        <GeneralCompassIcon className="flex-none w-6 h-6 mt-1 text-gray-500" />
        <div className="flex flex-col gap-1">
          <Heading variant={EHeadingStyleVariant.H5}>
            {intl.formatMessage({
              id: 'e7/Grm',
              defaultMessage: "Let's get your trip back on track",
              description:
                'Trips > Smart Match > Banner title encouraging user after a failed booking request',
            })}
          </Heading>
          <Text variant={ETextStyleVariant.SmallRegular}>
            {intl.formatMessage(
              {
                id: '/GzJzR',
                defaultMessage:
                  "We're sorry your previous request didn't work out. To save you time, we've found these specific matches that are available for {dateFrom} – {dateTo} and match your location & vehicle preferences.",
                description:
                  'Trips > Smart Match > Banner body with dates explaining that curated matches are shown based on user preferences',
              },
              {
                dateFrom: intl.formatDate(bid.from, {
                  month: 'short',
                  day: 'numeric',
                }),
                dateTo: intl.formatDate(bid.to, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }),
              },
            )}
          </Text>
        </div>
      </Banner>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
        {bid.bid_matches.map(match => (
          <BidMatchCard
            key={match.id}
            match={match}
            from={bid.from}
            to={bid.to}
            onDecline={handleDecline}
            onRequestToBook={handleRequestToBook}
          />
        ))}
      </div>
    </div>
  );
};

export default BidMatchCardContainer;
